import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useKycTokenValidation } from '@/hooks/useKyc';
import { AlertCircle, Loader2, ShieldX } from 'lucide-react';
import { useParams } from 'react-router-dom';

interface KycTokenValidationGuardProps {
  children: React.ReactNode;
}

/**
 * KYC Token Validation Guard
 * Validates KYC tokens before allowing access to KYC forms
 * Uses the KYC-specific validation endpoint
 */
export function KycTokenValidationGuard({
  children,
}: KycTokenValidationGuardProps) {
  const { token } = useParams<{ token: string }>();

  const {
    data: tokenValidation,
    isLoading,
    error,
    refetch,
  } = useKycTokenValidation(token || '');

  // Loading state
  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center p-4'>
        <Card className='w-full max-w-md'>
          <CardContent className='flex flex-col items-center justify-center py-8'>
            <Loader2 className='h-8 w-8 animate-spin text-primary mb-4' />
            <p className='text-sm text-muted-foreground text-center'>
              Validating KYC access token...
            </p>
            <p className='text-xs text-muted-foreground text-center mt-2'>
              Please wait while we verify your access permissions
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state or invalid token
  if (error || !tokenValidation?.success || !tokenValidation?.result?.valid) {
    const errorMessage =
      error?.message ||
      tokenValidation?.message ||
      'The provided KYC token is not valid or has expired. Please check your link and try again.';

    return (
      <div className='min-h-screen flex items-center justify-center p-4'>
        <Card className='w-full max-w-md'>
          <CardHeader className='text-center'>
            <div className='flex justify-center mb-4'>
              <ShieldX className='h-12 w-12 text-destructive' />
            </div>
            <CardTitle className='text-xl'>Access Denied</CardTitle>
            <CardDescription>
              Your KYC access token is invalid or has expired
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>

            {tokenValidation?.result?.expires_at && (
              <div className='p-3 bg-muted rounded-lg'>
                <p className='text-xs text-muted-foreground'>
                  <strong>Token Expiry:</strong>{' '}
                  {new Date(tokenValidation.result.expires_at).toLocaleString()}
                </p>
              </div>
            )}

            <div className='flex flex-col gap-2'>
              <Button
                variant='outline'
                onClick={() => refetch()}
                className='w-full'
              >
                Try Again
              </Button>
              <Button
                variant='ghost'
                onClick={() => window.close()}
                className='w-full'
              >
                Close Window
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid token - render children
  return <>{children}</>;
}

export default KycTokenValidationGuard;
